import { useAuth0 } from "@auth0/auth0-react";
import { Badge } from "@chakra-ui/react/badge";
import { Box } from "@chakra-ui/react/box";
import { Circle } from "@chakra-ui/react/circle";
import { Flex } from "@chakra-ui/react/flex";
import { Float } from "@chakra-ui/react/float";
import { useBreakpointValue, useDisclosure } from "@chakra-ui/react/hooks";
import { Icon } from "@chakra-ui/react/icon";
import { VStack } from "@chakra-ui/react/stack";
import { Text } from "@chakra-ui/react/text";
import ProfileAvatar from "components/profile/profile-avatar";
import StylePreferences from "components/style-preferences";

import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
} from "components/ui/menu";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import { useNotifications } from "hooks/queries/use-notifications";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import React, { useEffect, useState } from "react";
import {
  LuBellDot,
  LuCircleHelp,
  LuKeyRound,
  LuLogOut,
  LuPaintbrush,
  LuUserRoundCog,
} from "react-icons/lu";
import { setIsSelectorOpened } from "redux/features/merchant";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { endSession } from "util/endSession";
import { isMobileApp } from "util/methods";
import NotificationsModal from "./NotificationsModal";
import PasswordChangeModal from "./PasswordChangeModal";

interface ProfileSettingsProps {
  avatarOnly?: boolean;
}

const ProfileSettings = ({ avatarOnly = false }: ProfileSettingsProps) => {
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const dispatch = useAppDispatch();
  const { colorScheme } = useAppSelector((state) => state.theme);

  const { data: currentAgent } = useCurrentAgent();
  const { data: merchant } = useMerchant();
  const { isAuthenticated, logout } = useAuth0();
  const { data: notifications = [], isLoading: isLoadingNotifications } =
    useNotifications();
  const [loading, setLoading] = useState<boolean>(true);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const { loading: isLoadingMerchant } = useAppSelector(
    (state) => state.merchant
  );
  const passwordChangeModal = useDisclosure();
  const notificationsModal = useDisclosure();

  useEffect(() => {
    if (loading && !isLoadingMerchant && !isLoadingNotifications) {
      setLoading(false);
    }

    if (isLoadingNotifications && !isLoadingMerchant) {
      setLoading(true);
    }
  }, [isLoadingMerchant, isLoadingNotifications]);

  useEffect(() => {
    setUnreadCount(
      notifications.filter((notification) => !notification.read).length
    );
  }, [notifications]);

  const [isStyleModalOpen, setIsStyleModalOpen] = useState<boolean>(false);

  const hasMultipleMerchants = (currentAgent?.merchants?.length ?? 0) > 1;

  return (
    <>
      <MenuRoot>
        <MenuTrigger>
          <Flex
            alignItems="center"
            direction="column"
            cursor="pointer"
            data-testid="profile-menu-trigger"
          >
            <Box position="relative" display="inline-block">
              <ProfileAvatar
                profilePicture={currentAgent?.getPicture() ?? ""}
                name={currentAgent?.getFullName() ?? ""}
                avatarStyle={{
                  colorPalette: currentAgent?.color,
                }}
              />
              {unreadCount > 0 && (
                <Float>
                  <Circle
                    background="red.solid"
                    textStyle="caption"
                    color={{ _light: "white", _dark: "black" }}
                    size={6}
                  >
                    {unreadCount >= 100 ? "99+" : unreadCount}
                  </Circle>
                </Float>
              )}
            </Box>

            {!avatarOnly ? (
              <>
                <Text
                  data-private={true}
                  mt={2}
                  as="p"
                  textTransform="capitalize"
                  wordBreak="break-all"
                  color="white"
                >
                  {currentAgent ? currentAgent.name : null}
                </Text>
                <Text textStyle="caption" fontWeight="light" color="white">
                  {merchant?.name ?? ""}
                </Text>
              </>
            ) : null}
          </Flex>
        </MenuTrigger>
        <MenuContent zIndex={999999}>
          <VStack my={2} alignItems="start">
            <Text
              data-private={true}
              pl={4}
              fontWeight="bold"
              color="fg.subtle"
            >
              {currentAgent ? currentAgent.getFullName() : null}
            </Text>
            <Text data-private={true} pl={4} color="fg.subtle">
              {currentAgent ? currentAgent.email : null}
            </Text>
            {hasMultipleMerchants && isBaseSize ? (
              <MenuItem
                value="Switch Account"
                onSelect={() => {
                  dispatch(setIsSelectorOpened(true));
                }}
              >
                {/* FREO: closest Lucide match for AiOutlineUserSwitch */}
                <Icon
                  width="1.5rem"
                  height="1.5rem"
                  as={LuUserRoundCog}
                  color={{
                    _light: `${colorScheme}.600`,
                    _dark: `${colorScheme}.200`,
                  }}
                />{" "}
                Switch Account
              </MenuItem>
            ) : null}
            <MenuItem
              value="Notifications"
              onSelect={notificationsModal.onOpen}
            >
              <Icon
                width="1.5rem"
                height="1.5rem"
                as={LuBellDot}
                color={{
                  _light: `${colorScheme}.600`,
                  _dark: `${colorScheme}.200`,
                }}
              />
              <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Text>Notifications</Text>
                {unreadCount > 0 && (
                  <Circle
                    zIndex="1"
                    height="1.5rem"
                    width="1.5rem"
                    background={{
                      _light: `${colorScheme}.700`,
                      _dark: `${colorScheme}.400`,
                    }}
                    textStyle="caption"
                    color={{ _light: "white", _dark: "black" }}
                  >
                    {unreadCount >= 100 ? "99+" : unreadCount}
                  </Circle>
                )}
              </Flex>
            </MenuItem>
            <MenuItem
              value="style"
              onSelect={() => {
                setIsStyleModalOpen(true);
              }}
            >
              <Icon
                width="1.5rem"
                height="1.5rem"
                as={LuPaintbrush}
                color={{
                  _light: `${colorScheme}.600`,
                  _dark: `${colorScheme}.200`,
                }}
              />{" "}
              Style
            </MenuItem>
            <MenuItem
              value="FAQ"
              onSelect={() => {
                const faqUrl = "https://www.getfuzey.com/resources/faq";

                if (isMobileApp()) {
                  window.natively.openExternalURL(faqUrl);
                } else {
                  window.open(faqUrl, "_blank");
                }
              }}
            >
              <Icon
                width="1.5rem"
                height="1.5rem"
                as={LuCircleHelp}
                color={{
                  _light: `${colorScheme}.600`,
                  _dark: `${colorScheme}.200`,
                }}
              />{" "}
              FAQ
            </MenuItem>
            <MenuItem
              value="Change Password"
              onSelect={() => {
                passwordChangeModal.onOpen();
              }}
            >
              <Icon
                width="1.5rem"
                height="1.5rem"
                as={LuKeyRound}
                color={{
                  _light: `${colorScheme}.600`,
                  _dark: `${colorScheme}.200`,
                }}
              />{" "}
              Change Password
            </MenuItem>
            {isAuthenticated ? (
              <MenuItem
                value="Log out"
                onSelect={() => {
                  endSession(logout);
                }}
              >
                <Icon
                  width="1.5rem"
                  height="1.5rem"
                  as={LuLogOut}
                  color={{
                    _light: `${colorScheme}.600`,
                    _dark: `${colorScheme}.200`,
                  }}
                  css={{
                    "& path": {
                      fill: "transparent",
                    },
                  }}
                />{" "}
                Log out
              </MenuItem>
            ) : null}
          </VStack>
        </MenuContent>
      </MenuRoot>
      <StylePreferences
        isOpen={isStyleModalOpen}
        setIsOpen={setIsStyleModalOpen}
      />
      <NotificationsModal
        isOpen={notificationsModal.open}
        onClose={notificationsModal.onClose}
      />
      <PasswordChangeModal
        isOpen={passwordChangeModal.open}
        onClose={passwordChangeModal.onClose}
      />
    </>
  );
};

export default ProfileSettings;
